import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import EntityNameSchema from "legal-entities/entity-names/_schema/entity-name-schema";
import EntityName from "legal-entities/entity-names/entity-name";
import { z } from "zod";

export type UserEntityInfoJSON = z.infer<typeof UserEntityInfoSchema.UserEntityInfoJSONZ>;
export type UserEntityInfoParams = z.infer<typeof UserEntityInfoSchema.UserEntityInfoZ>;

export default class UserEntityInfoSchema {
  public static readonly UserEntityInfoJSONZ = z.object({
    entityId: z.uuid(),
    entityName: z.string(),
    isAdmin: z.boolean(),
    isManager: z.boolean(),
    isOfficer: z.boolean(),
    entityVendorFlag: z.boolean().nullish(),
    entityClientFlag: z.boolean().nullish(),
  });

  public static readonly UserEntityInfoZ = z.object({
    entityId: z.instanceof(Guid),
    entityName: z.instanceof(EntityName),
    isAdmin: z.boolean(),
    isManager: z.boolean(),
    isOfficer: z.boolean(),
    entityVendorFlag: z.boolean().optional(),
    entityClientFlag: z.boolean().optional(),
  });

  public static validate(values: unknown): UserEntityInfoParams {
    return parseOrThrow(UserEntityInfoSchema.UserEntityInfoZ, values, {
      logError: true,
      schemaName: "UserEntityInfoSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): UserEntityInfoJSON {
    return parseOrThrow(UserEntityInfoSchema.UserEntityInfoJSONZ, json, {
      logError: true,
      schemaName: "UserEntityInfoSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is UserEntityInfoParams {
    try {
      UserEntityInfoSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is UserEntityInfoJSON {
    try {
      UserEntityInfoSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: unknown): UserEntityInfoParams {
    const parsed = parseOrThrow(UserEntityInfoSchema.UserEntityInfoJSONZ, json, {
      logError: true,
      schemaName: "UserEntityInfoSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return UserEntityInfoSchema.codec.decode(parsed);
  }

  public static deserializeArray(jsonArray: unknown): UserEntityInfoParams[] {
    const parsedArray = parseOrThrow(z.array(UserEntityInfoSchema.UserEntityInfoJSONZ), jsonArray, {
      logError: true,
      schemaName: "UserEntityInfoSchema",
      operation: "deserializeArray",
      payloadSample: jsonArray,
    });
    return parsedArray.map((parsed) => UserEntityInfoSchema.codec.decode(parsed));
  }

  public static serialize(legalEntity: UserEntityInfoParams): UserEntityInfoJSON {
    const parsed = UserEntityInfoSchema.validate(legalEntity);
    return UserEntityInfoSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    UserEntityInfoSchema.UserEntityInfoJSONZ,
    UserEntityInfoSchema.UserEntityInfoZ,
    {
      decode: (data) => ({
        entityId: Guid.fromJSON(data.entityId)!,
        entityName: new EntityName(EntityNameSchema.deserialize(data.entityName)),
        isAdmin: data.isAdmin,
        isManager: data.isManager,
        isOfficer: data.isOfficer,
        entityVendorFlag: data.entityVendorFlag ?? false,
        entityClientFlag: data.entityClientFlag ?? false,
      }),
      encode: (info) => ({
        entityId: info.entityId.toJSON(),
        entityName: info.entityName.toJSON(),
        isAdmin: info.isAdmin,
        isManager: info.isManager,
        isOfficer: info.isOfficer,
        entityVendorFlag: info.entityVendorFlag,
        entityClientFlag: info.entityClientFlag,
      }),
    }
  );
}
